import React, { useEffect, useRef, useState } from "react";
import {
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ImageIcon,
  ImagePlus,
  Loader2,
  MoreHorizontal,
  Plus,
  Sparkles,
  Trash2,
  User,
} from "lucide-react";
import OptionSheet from "./OptionSheet";
import { supabase } from "../../lib/supabase";
import { GENERATE_AUTH_TOKEN } from "../../config";
import { isHeicFile, convertHeicToJpeg } from "../../utils/heicConvert";

const SKIN_TONE_OPTIONS = [
  { label: "Muy clara", color: "#FFDFC4" },
  { label: "Clara", color: "#F0C8A0" },
  { label: "Beige", color: "#DEB887" },
  { label: "Trigueña", color: "#CD853F" },
  { label: "Oscura", color: "#8B4513" },
  { label: "Muy oscura", color: "#3C2414" },
];

const HAIR_COLOR_OPTIONS = [
  { label: "Negro", color: "#232323" },
  { label: "Castaño oscuro", color: "#4B2E09" },
  { label: "Castaño claro", color: "#A0522D" },
  { label: "Rubio", color: "#F8E49B" },
  { label: "Colorado", color: "#B55239" },
];

type SavedModel = { id: number; nombre: string; signedUrl?: string };

interface ModeloGuardadoOptionProps {
  open: boolean;
  onClose: () => void;
  selectedModelId: number | null;
  onSelect: (id: number | null, nombre?: string) => void;
}

const ModeloGuardadoOption: React.FC<ModeloGuardadoOptionProps> = ({
  open,
  onClose,
  selectedModelId,
  onSelect,
}) => {
  const [savedModels, setSavedModels] = useState<SavedModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [localSelectedId, setLocalSelectedId] = useState<number | null>(
    selectedModelId,
  );
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [deletingModelId, setDeletingModelId] = useState<number | null>(null);

  // Add model form
  const [showAddForm, setShowAddForm] = useState<
    false | "chooser" | "real" | "ia" | "historial"
  >(false);
  const [addName, setAddName] = useState("");
  const [addFile, setAddFile] = useState<File | null>(null);
  const [addPreviewUrl, setAddPreviewUrl] = useState<string | null>(null);
  const [consentA, setConsentA] = useState(false);
  const [consentB, setConsentB] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [convertingHeic, setConvertingHeic] = useState(false);

  // AI model form
  const [aiName, setAiName] = useState("");
  const [aiGenero, setAiGenero] = useState("");
  const [aiEdad, setAiEdad] = useState("");
  const [aiTipoCuerpo, setAiTipoCuerpo] = useState("");
  const [aiTonoPiel, setAiTonoPiel] = useState("");
  const [aiColorPelo, setAiColorPelo] = useState("");
  const [aiLargoPelo, setAiLargoPelo] = useState("");
  const [aiDescripcion, setAiDescripcion] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiResultB64, setAiResultB64] = useState<string | null>(null);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Historial (desde imagen generada) form
  type GenImageItem = {
    id: string;
    fileName: string;
    thumbUrl: string;
    fullUrl: string;
  };
  const [genImages, setGenImages] = useState<GenImageItem[]>([]);
  const [genLoading, setGenLoading] = useState(false);
  const [genLoadingMore, setGenLoadingMore] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [genHasMore, setGenHasMore] = useState(true);
  const [genPage, setGenPage] = useState(0);
  const GEN_PAGE_SIZE = 32;
  const genContainerRef = useRef<HTMLDivElement | null>(null);
  const genLoadMoreRef = useRef<HTMLDivElement | null>(null);
  const genLoadInFlightRef = useRef<boolean>(false);
  const [selectedGenImage, setSelectedGenImage] = useState<GenImageItem | null>(
    null,
  );
  const [savingModelFromImage, setSavingModelFromImage] = useState(false);
  const [historialSuccess, setHistorialSuccess] = useState(false);

  const snapshotRef = useRef<number | null>(null);

  useEffect(() => {
    if (open) {
      snapshotRef.current = selectedModelId;
      setLocalSelectedId(selectedModelId);
      if (savedModels.length === 0 && !loading) fetchModels();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetAddState = () => {
    setAddName("");
    setAddFile(null);
    setAddPreviewUrl(null);
    setConsentA(false);
    setConsentB(false);
    setSaving(false);
    setAddError(null);
    setAiName("");
    setAiGenero("");
    setAiEdad("");
    setAiTipoCuerpo("");
    setAiTonoPiel("");
    setAiColorPelo("");
    setAiLargoPelo("");
    setAiDescripcion("");
    setAiGenerating(false);
    setAiResultB64(null);
    setAiSaving(false);
    setAiError(null);
    setSelectedGenImage(null);
    setSavingModelFromImage(false);
    setHistorialSuccess(false);
  };

  const fetchModels = async () => {
    try {
      setLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      const idCompany = (profile as any)?.id_company;
      if (!idCompany) return;
      const { data, error } = await supabase
        .from("modelos")
        .select("id, nombre, foto_file_name")
        .eq("id_company", idCompany)
        .order("nombre");
      if (error) throw error;
      const rows = (data as any[]) || [];
      const withSigned = await Promise.all(
        rows.map(async (m) => {
          let signedUrl: string | undefined;
          const fileName = m?.foto_file_name;
          if (fileName) {
            try {
              const { data: signed } = await supabase.storage
                .from("modelos")
                .createSignedUrl(`${idCompany}/full/${fileName}`, 3600);
              if (signed?.signedUrl) signedUrl = signed.signedUrl;
            } catch {}
          }
          return { id: m.id as number, nombre: m.nombre as string, signedUrl };
        }),
      );
      setSavedModels(withSigned);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (file: File) => {
    if (isHeicFile(file)) {
      try {
        setConvertingHeic(true);
        const { file: converted, url } = await convertHeicToJpeg(file, 0.85);
        setAddFile(converted);
        setAddPreviewUrl(url);
        setAddError(null);
      } catch {
        setAddError(
          "No pudimos procesar la imagen. Probá con una imagen en formato PNG o JPG.",
        );
      } finally {
        setConvertingHeic(false);
      }
    } else {
      setAddFile(file);
      setAddPreviewUrl(URL.createObjectURL(file));
      setAddError(null);
    }
  };

  const handleSaveReal = async () => {
    try {
      setAddError(null);
      if (!addName.trim()) throw new Error("Ingresá un nombre para el modelo");
      if (!addFile) throw new Error("Subí una imagen del rostro");
      if (!consentA || !consentB)
        throw new Error("Debés aceptar ambas confirmaciones");
      setSaving(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      const idCompany = (profile as any)?.id_company;
      if (!idCompany) throw new Error("No se encontró la empresa");
      const ext = (addFile.name.split(".").pop() || "jpg").toLowerCase();
      const base = addName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      const fileName = `${Date.now()}-${base || "modelo"}.${ext}`;
      const targetPath = `${idCompany}/full/${fileName}`;
      const { error: upErr } = await supabase.storage
        .from("modelos")
        .upload(targetPath, addFile, {
          cacheControl: "3600",
          upsert: false,
          contentType: addFile.type || "image/jpeg",
        });
      if (upErr) throw upErr;
      const { error: insErr } = await supabase.from("modelos").insert({
        id_company: idCompany,
        nombre: addName.trim(),
        foto_file_name: fileName,
      });
      if (insErr) throw insErr;
      await fetchModels();
      setShowAddForm(false);
      resetAddState();
    } catch (e: any) {
      console.error("Error subiendo modelo:", e);
      setAddError(e?.message || "No se pudo guardar el modelo");
    } finally {
      setSaving(false);
    }
  };

  const handleAiGenerate = async () => {
    try {
      setAiError(null);
      setAiGenerating(true);
      setAiResultB64(null);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");
      const response = await fetch(
        "https://ai-agency-dt7d.onrender.com/webhook/generar_modelo",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: GENERATE_AUTH_TOKEN,
          },
          body: JSON.stringify({
            user_id: user.id,
            nombre: aiName.trim(),
            genero: aiGenero,
            edad: aiEdad,
            tipo_cuerpo: aiTipoCuerpo,
            tono_piel: aiTonoPiel,
            color_pelo: aiColorPelo,
            largo_pelo: aiLargoPelo,
            descripcion: aiDescripcion,
          }),
        },
      );
      if (!response.ok) throw new Error("Error al generar el modelo");
      const data = await response.json();
      if (data.response !== "success" || !data.image)
        throw new Error("No se recibió la imagen generada");
      setAiResultB64(data.image);
    } catch (e: any) {
      console.error("Error generando modelo con IA:", e);
      setAiError(e?.message || "No se pudo generar el modelo");
    } finally {
      setAiGenerating(false);
    }
  };

  const loadGeneratedImages = async (isMore = false) => {
    if (genLoadInFlightRef.current) return;
    genLoadInFlightRef.current = true;
    try {
      setGenError(null);
      if (isMore) setGenLoadingMore(true);
      else setGenLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      const idCompany = (profile as any)?.id_company as string | undefined;
      if (!idCompany) throw new Error("No se encontró la empresa");
      const startIndex = isMore ? genPage * GEN_PAGE_SIZE : 0;
      const endIndex = startIndex + GEN_PAGE_SIZE - 1;
      const { data: imagesData, error } = await supabase
        .from("imagenes_generadas")
        .select("id, file_name, created_at, active, id_company")
        .eq("active", true)
        .eq("id_company", idCompany)
        .order("created_at", { ascending: false })
        .range(startIndex, endIndex);
      if (error) throw error;
      const processed: GenImageItem[] = await Promise.all(
        ((imagesData as any[]) || []).map(async (img) => {
          const optPath = `${idCompany}/opt/${img.file_name}`;
          const fullPath = `${idCompany}/full/${img.file_name}`;
          let thumbUrl = "";
          let fullUrl = "";
          try {
            const { data: optSigned } = await supabase.storage
              .from("generadas")
              .createSignedUrl(optPath, 3600);
            if (optSigned?.signedUrl) thumbUrl = optSigned.signedUrl;
            const { data: fullSigned } = await supabase.storage
              .from("generadas")
              .createSignedUrl(fullPath, 3600);
            if (fullSigned?.signedUrl) fullUrl = fullSigned.signedUrl;
            if (!thumbUrl && fullUrl) thumbUrl = fullUrl;
            if (!fullUrl && thumbUrl) fullUrl = thumbUrl;
          } catch {}
          return {
            id: String(img.id),
            fileName: img.file_name as string,
            thumbUrl,
            fullUrl,
          };
        }),
      );
      if (isMore) {
        setGenImages((prev) => {
          const merged = [...prev, ...processed];
          const seen = new Set<string>();
          const deduped: GenImageItem[] = [];
          for (const item of merged) {
            if (!seen.has(item.id)) {
              seen.add(item.id);
              deduped.push(item);
            }
          }
          return deduped;
        });
        setGenPage((prev) => prev + 1);
      } else {
        setGenImages(() => {
          const seen = new Set<string>();
          const deduped: GenImageItem[] = [];
          for (const item of processed) {
            if (!seen.has(item.id)) {
              seen.add(item.id);
              deduped.push(item);
            }
          }
          return deduped;
        });
        setGenPage(1);
      }
      setGenHasMore(((imagesData as any[]) || []).length === GEN_PAGE_SIZE);
    } catch (e: any) {
      console.error("Error cargando imágenes generadas:", e);
      setGenError(
        e?.message ||
          "Error al cargar imágenes. Por favor, volvé a intentarlo.",
      );
    } finally {
      setGenLoading(false);
      setGenLoadingMore(false);
      genLoadInFlightRef.current = false;
    }
  };

  const handleSaveFromGenerated = async () => {
    if (!selectedGenImage) return;
    try {
      setHistorialSuccess(false);
      setSavingModelFromImage(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");
      const resp = await fetch(
        "https://ai-agency-dt7d.onrender.com/webhook/guardar_modelo_generado",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: GENERATE_AUTH_TOKEN,
          },
          body: JSON.stringify({
            user_id: user.id,
            imagen_id: selectedGenImage.id,
          }),
        },
      );
      const data = await resp.json().catch(() => ({}));
      if (data?.response === "success") {
        await fetchModels();
        setSavingModelFromImage(false);
        setHistorialSuccess(true);
        setTimeout(() => {
          setShowAddForm(false);
          resetAddState();
        }, 1500);
      } else {
        throw new Error("No se pudo añadir el modelo");
      }
    } catch (e: any) {
      console.error("Error al añadir modelo desde imagen generada:", e);
      setGenError(e?.message || "Error al añadir el modelo");
      setSavingModelFromImage(false);
    }
  };

  // Lazy load generated images when entering historial view
  useEffect(() => {
    if (
      showAddForm === "historial" &&
      genImages.length === 0 &&
      !genLoading &&
      !genLoadInFlightRef.current
    ) {
      loadGeneratedImages(false);
    }
  }, [showAddForm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll observer for generated images grid
  useEffect(() => {
    if (showAddForm !== "historial") return;
    const root = genContainerRef.current;
    const sentinel = genLoadMoreRef.current;
    if (!root || !sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            genHasMore &&
            !genLoading &&
            !genLoadingMore &&
            !genLoadInFlightRef.current
          ) {
            loadGeneratedImages(true);
          }
        });
      },
      { root, rootMargin: "200px 0px", threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [showAddForm, genHasMore, genLoading, genLoadingMore]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAiSave = async () => {
    try {
      if (!aiResultB64) return;
      setAiError(null);
      setAiSaving(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      const idCompany = (profile as any)?.id_company;
      if (!idCompany) throw new Error("No se encontró la empresa");
      const byteChars = atob(aiResultB64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++)
        byteNumbers[i] = byteChars.charCodeAt(i);
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "image/png" });
      const base = aiName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      const fileName = `${Date.now()}-${base || "modelo-ia"}.png`;
      const targetPath = `${idCompany}/full/${fileName}`;
      const { error: upErr } = await supabase.storage
        .from("modelos")
        .upload(targetPath, blob, {
          cacheControl: "3600",
          upsert: false,
          contentType: "image/png",
        });
      if (upErr) throw upErr;
      const { error: insErr } = await supabase.from("modelos").insert({
        id_company: idCompany,
        nombre: aiName.trim(),
        foto_file_name: fileName,
      });
      if (insErr) throw insErr;
      await fetchModels();
      setShowAddForm(false);
      resetAddState();
    } catch (e: any) {
      console.error("Error guardando modelo IA:", e);
      setAiError(e?.message || "No se pudo guardar el modelo");
    } finally {
      setAiSaving(false);
    }
  };

  const handleDelete = async (modelId: number) => {
    const model = savedModels.find((m) => m.id === modelId);
    if (!model) return;
    setOpenMenuId(null);
    setDeletingModelId(modelId);
    // Wait for fade-out animation
    await new Promise((r) => setTimeout(r, 300));
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      const idCompany = (profile as any)?.id_company;
      if (!idCompany) return;
      const { data: modelRow } = await supabase
        .from("modelos")
        .select("foto_file_name")
        .eq("id", modelId)
        .single();
      const fileName = (modelRow as any)?.foto_file_name;
      if (fileName)
        await supabase.storage
          .from("modelos")
          .remove([`${idCompany}/full/${fileName}`]);
      await supabase.from("modelos").delete().eq("id", modelId);
      if (localSelectedId === modelId) setLocalSelectedId(null);
      setSavedModels((prev) => prev.filter((m) => m.id !== modelId));
    } catch (e) {
      console.error("Error deleting model:", e);
    }
    setDeletingModelId(null);
  };

  const handleClose = () => {
    setLocalSelectedId(snapshotRef.current);
    setShowAddForm(false);
    resetAddState();
    onClose();
  };

  const handleAccept = () => {
    const selected = savedModels.find((m) => m.id === localSelectedId);
    onSelect(localSelectedId, selected?.nombre || undefined);
    setShowAddForm(false);
    resetAddState();
  };

  // Pill button helper for AI model form
  const aiPill = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>,
    label: string,
  ) => (
    <button
      key={label}
      type="button"
      onClick={() => setter((prev) => (prev === label ? "" : label))}
      className={`px-4 h-10 rounded-full border text-[15px] ${
        value === label
          ? "bg-brand-primary text-white border-brand-primary"
          : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
      }`}
    >
      {label}
    </button>
  );

  // Main list content
  const listContent = (
    <>
      <p className="text-[15px] text-neutral-500 mb-4">
        Usá un modelo que ya tengas guardado en tu marca. Se recomienda usar
        Prioridad en Calidad para mantener mejor la consistencia de los rasgos
        faciales.
      </p>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => setShowAddForm("chooser")}
            className="relative rounded-md border border-neutral-200 overflow-hidden transition-colors hover:border-neutral-300 group bg-white"
          >
            <div className="w-full h-32 bg-neutral-50 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-brand-primary text-white flex items-center justify-center transition-colors group-hover:bg-brand-primary-hover">
                <Plus className="w-6 h-6" />
              </div>
            </div>
            <p className="text-[15px] text-neutral-900 p-2 font-medium">
              Añadir modelo
            </p>
          </button>
          {savedModels.map((m) => {
            const selected = localSelectedId === m.id;
            return (
              <div
                key={m.id}
                onClick={() => {
                  if (openMenuId !== null) {
                    setOpenMenuId(null);
                    return;
                  }
                  setLocalSelectedId(selected ? null : m.id);
                }}
                className={`relative rounded-md border bg-white cursor-pointer transition-all duration-300 ${
                  deletingModelId === m.id
                    ? "opacity-0 scale-95"
                    : "opacity-100 scale-100"
                } ${openMenuId === m.id ? "z-30" : ""} ${
                  selected
                    ? "border-brand-primary ring-2 ring-brand-primary"
                    : "border-neutral-200 hover:border-neutral-300"
                }`}
              >
                <div className="relative overflow-hidden rounded-t-sm">
                  {m.signedUrl ? (
                    <img
                      src={m.signedUrl}
                      alt={m.nombre || "Modelo"}
                      loading="lazy"
                      className="w-full h-32 object-cover"
                    />
                  ) : (
                    <div className="w-full h-32 bg-neutral-100 flex items-center justify-center">
                      <User className="w-8 h-8 text-neutral-400" />
                    </div>
                  )}
                  {selected && (
                    <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary text-white">
                      <Check className="w-4 h-4" />
                    </span>
                  )}
                </div>
                <div className="flex items-center p-2">
                  <p className="text-[15px] text-neutral-900 font-medium truncate flex-1">
                    {m.nombre || `Modelo #${m.id}`}
                  </p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(openMenuId === m.id ? null : m.id);
                    }}
                    className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full hover:bg-neutral-100 transition-colors"
                  >
                    <MoreHorizontal className="w-3.5 h-3.5 text-neutral-400" />
                  </button>
                </div>
                {openMenuId === m.id && (
                  <div
                    className="absolute bottom-0 right-2 translate-y-full z-20 mt-1 bg-white rounded-md shadow-lg border border-neutral-200 py-1 min-w-[140px]"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={() => handleDelete(m.id)}
                      className="w-full text-left px-3 py-2 text-[13px] text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Eliminar
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  // Chooser content
  const chooserContent = (
    <>
      <p className="text-[15px] text-neutral-500 mb-4">
        Elegí cómo querés crear tu modelo.
      </p>
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => setShowAddForm("real")}
          className="w-full flex items-center gap-4 p-4 bg-surface-card border border-neutral-200 rounded-lg hover:border-neutral-300 transition-colors text-left"
        >
          <div className="w-12 h-12 rounded-md bg-neutral-100 flex items-center justify-center shrink-0">
            <ImagePlus className="w-6 h-6 text-neutral-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-semibold text-brand-black/90">
              Añadir modelo real
            </p>
            <p className="text-[13px] text-neutral-500 mt-0.5">
              Subí una foto del rostro de una persona real.
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-neutral-400 shrink-0" />
        </button>
        <button
          type="button"
          onClick={() => setShowAddForm("ia")}
          className="w-full flex items-center gap-4 p-4 bg-surface-card border border-neutral-200 rounded-lg hover:border-neutral-300 transition-colors text-left"
        >
          <div className="w-12 h-12 rounded-md bg-neutral-100 flex items-center justify-center shrink-0">
            <Sparkles className="w-6 h-6 text-neutral-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-semibold text-brand-black/90">
              Crear modelo con IA
            </p>
            <p className="text-[13px] text-neutral-500 mt-0.5">
              Describí las características y generamos un modelo para vos.
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-neutral-400 shrink-0" />
        </button>
        <button
          type="button"
          onClick={() => setShowAddForm("historial")}
          className="w-full flex items-center gap-4 p-4 bg-surface-card border border-neutral-200 rounded-lg hover:border-neutral-300 transition-colors text-left"
        >
          <div className="w-12 h-12 rounded-md bg-neutral-100 flex items-center justify-center shrink-0">
            <ImageIcon className="w-6 h-6 text-neutral-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-semibold text-brand-black/90">
              Desde imagen generada
            </p>
            <p className="text-[13px] text-neutral-500 mt-0.5">
              Usá una imagen ya generada del historial como modelo.
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-neutral-400 shrink-0" />
        </button>
      </div>
    </>
  );

  // Historial (from generated image) content
  const historialFormContent = (
    <div ref={genContainerRef} className="relative">
      {(savingModelFromImage || historialSuccess) && (
        <div className="absolute inset-0 z-20 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center text-center p-4 rounded-lg">
          {savingModelFromImage && !historialSuccess && (
            <>
              <Loader2 className="w-10 h-10 animate-spin text-brand-primary" />
              <p className="mt-3 text-[15px] text-neutral-700">
                Analizando modelo...
              </p>
              <p className="mt-1 text-[13px] text-neutral-500">
                Esto puede tardar unos segundos.
              </p>
            </>
          )}
          {historialSuccess && (
            <>
              <CheckCircle2 className="w-12 h-12 text-green-600" />
              <p className="mt-2 text-[15px] text-neutral-800">
                Modelo añadido
              </p>
            </>
          )}
        </div>
      )}
      {selectedGenImage ? (
        <div className="flex flex-col items-center gap-4">
          <button
            type="button"
            onClick={() => setSelectedGenImage(null)}
            className="self-start inline-flex items-center gap-1 text-[13px] text-brand-primary hover:opacity-70 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4" />
            Elegir otra
          </button>
          <div className="w-full max-w-[320px] aspect-square rounded-lg border border-neutral-200 overflow-hidden bg-neutral-50">
            <img
              src={selectedGenImage.fullUrl || selectedGenImage.thumbUrl}
              alt="Imagen seleccionada"
              className="w-full h-full object-contain"
            />
          </div>
          <p className="text-[13px] text-neutral-500 text-center">
            Se extraerá el rostro del modelo presente en esta imagen.
          </p>
          {genError && (
            <p className="text-[13px] text-red-600 mb-3">{genError}</p>
          )}
        </div>
      ) : (
        <>
          <p className="text-[15px] text-neutral-500 mb-4">
            Elegí una imagen del historial para extraer el rostro del modelo.
          </p>
          {genError && (
            <p className="text-[13px] text-red-600 mb-3">{genError}</p>
          )}
          {genLoading && genImages.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
            </div>
          ) : genImages.length === 0 ? (
            <div className="text-center py-12 text-[15px] text-neutral-500">
              No hay imágenes generadas todavía.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {genImages.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setSelectedGenImage(g)}
                  className="relative rounded-md overflow-hidden border border-neutral-200 hover:border-neutral-300 transition-colors focus:outline-none"
                >
                  {g.thumbUrl ? (
                    <img
                      src={g.thumbUrl}
                      alt={`Img ${g.id}`}
                      className="w-full aspect-square object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full aspect-square bg-neutral-100" />
                  )}
                </button>
              ))}
            </div>
          )}
          {(genHasMore || genLoadingMore) && (
            <div
              ref={genLoadMoreRef}
              className="py-3 text-center text-[13px] text-neutral-500"
            >
              {genLoadingMore ? (
                <span className="inline-flex items-center">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Cargando más...
                </span>
              ) : (
                ""
              )}
            </div>
          )}
        </>
      )}
    </div>
  );

  // Real model form content
  const realFormContent = (
    <div className="space-y-4">
      <div>
        <label className="block text-[15px] font-medium text-neutral-700 mb-1">
          Nombre del modelo
        </label>
        <input
          type="text"
          value={addName}
          onChange={(e) => setAddName(e.target.value)}
          placeholder="Ej: María, Juan, etc."
          className="w-full rounded-md border border-neutral-200 p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary"
        />
      </div>
      <div>
        <label className="block text-[15px] font-medium text-neutral-700 mb-1">
          Foto del rostro
        </label>
        {!addFile && (
          <div className="bg-surface-card border border-neutral-200 rounded-lg p-3 sm:p-4 mb-3">
            <div className="flex gap-3">
              <div className="w-24 h-24 shrink-0 rounded-lg border border-neutral-200 bg-surface-card overflow-hidden relative">
                <img
                  src="/images/cara_modelo_ejemplo.png"
                  alt="Ejemplo"
                  className="w-full h-full object-cover"
                />
                <span className="absolute bottom-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-success text-white shadow-sm">
                  <Check className="w-3.5 h-3.5" />
                </span>
              </div>
              <ul className="text-[13px] text-neutral-600 list-disc pl-4 space-y-1">
                <li>
                  Subí una foto en donde se vea claramente el rostro de la
                  persona.
                </li>
                <li>
                  Evitá fotos con lentes de sol, máscaras o elementos que tapen
                  la cara.
                </li>
                <li>Usá una foto con buena iluminación y fondo simple.</li>
              </ul>
            </div>
          </div>
        )}
        {convertingHeic && !addFile && (
          <div className="bg-surface-card border border-neutral-200 rounded-lg p-4 mb-3 flex items-center justify-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
            <p className="text-[15px] text-neutral-600">Subiendo imagen...</p>
          </div>
        )}
        {addFile && (
          <div className="bg-surface-card border border-neutral-200 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-3">
              {addPreviewUrl ? (
                <img
                  src={addPreviewUrl}
                  alt="Preview"
                  className="w-20 h-20 object-cover rounded-md shrink-0"
                />
              ) : (
                <div className="w-20 h-20 rounded-md shrink-0 bg-neutral-100 flex items-center justify-center">
                  <span className="material-symbols-rounded text-[24px] text-neutral-400">
                    image
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[15px] text-neutral-700 truncate">
                  {addFile.name}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setAddFile(null);
                    setAddPreviewUrl(null);
                  }}
                  className="text-[13px] text-error hover:opacity-70 mt-1"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}
        <input
          type="file"
          id="add-model-file-comp"
          accept="image/*,.heic,.heif"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
            e.target.value = "";
          }}
          className="hidden"
        />
        <label
          htmlFor="add-model-file-comp"
          className={`inline-flex w-full justify-center items-center h-10 px-4 shadow-sm text-[15px] font-medium rounded-md cursor-pointer transition-colors ${
            convertingHeic
              ? "pointer-events-none opacity-50 border border-neutral-200 text-neutral-900 bg-surface-card"
              : !addFile
                ? "bg-brand-primary text-white hover:bg-brand-primary-hover"
                : "border border-neutral-200 text-neutral-900 bg-surface-card hover:bg-neutral-100"
          }`}
        >
          <ImagePlus className="w-4 h-4 mr-2" />
          {convertingHeic
            ? "Subiendo..."
            : !addFile
              ? "Subir foto del rostro"
              : "Cambiar imagen"}
        </label>
      </div>
      <div className="space-y-3">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={consentA}
            onChange={(e) => setConsentA(e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-neutral-300 text-brand-primary focus:ring-brand-primary"
          />
          <span className="text-[15px] text-neutral-600">
            Confirmo que tengo los derechos de uso de esta imagen.
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={consentB}
            onChange={(e) => setConsentB(e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-neutral-300 text-brand-primary focus:ring-brand-primary"
          />
          <span className="text-[15px] text-neutral-600">
            Acepto que esta imagen sea utilizada para generar contenido con IA.
          </span>
        </label>
      </div>
      {addError && <p className="text-[15px] text-error">{addError}</p>}
    </div>
  );

  // AI model form content
  const aiFormContent = (
    <>
      {aiGenerating ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
          <p className="text-[15px] text-neutral-500">Generando modelo...</p>
        </div>
      ) : aiResultB64 ? (
        <div className="flex flex-col items-center gap-4">
          <div className="w-full max-w-[280px] aspect-square rounded-lg border border-neutral-200 overflow-hidden bg-neutral-50">
            <img
              src={`data:image/png;base64,${aiResultB64}`}
              alt={aiName || "Modelo generado"}
              className="w-full h-full object-cover"
            />
          </div>
          <p className="text-[15px] font-medium text-brand-black/90">
            {aiName}
          </p>
          <button
            type="button"
            onClick={handleAiGenerate}
            className="text-[13px] text-brand-primary hover:opacity-70 transition-opacity"
          >
            Volver a generar
          </button>
          {aiError && <p className="text-[13px] text-error">{aiError}</p>}
        </div>
      ) : (
        <>
          <p className="text-[15px] text-neutral-500 mb-4">
            Definí las características del modelo que querés generar.
          </p>
          <div className="space-y-5">
            <div>
              <label className="block text-[15px] text-neutral-700 mb-2">
                Nombre del modelo
              </label>
              <input
                type="text"
                value={aiName}
                onChange={(e) => setAiName(e.target.value)}
                placeholder="Ej: María, Juan, etc."
                className="w-full rounded-md border border-neutral-200 p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>
            <div>
              <label className="block text-[15px] text-neutral-700 mb-2">
                Género
              </label>
              <div className="flex gap-2">
                {["Hombre", "Mujer"].map((g) =>
                  aiPill(aiGenero, setAiGenero, g),
                )}
              </div>
            </div>
            <div>
              <label className="block text-[15px] text-neutral-700 mb-2">
                Edad
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  "Bebé",
                  "Niño",
                  "Joven (25 años)",
                  "Adulto/a(30 a 40 años)",
                  "Mayor",
                ].map((e) => aiPill(aiEdad, setAiEdad, e))}
              </div>
            </div>
            <div>
              <label className="block text-[15px] text-neutral-700 mb-2">
                Tipo de cuerpo
              </label>
              <div className="flex flex-wrap gap-2">
                {["Promedio", "Talle Grande", "Talle Extra Grande"].map((c) =>
                  aiPill(aiTipoCuerpo, setAiTipoCuerpo, c),
                )}
              </div>
            </div>
            <div>
              <label className="block text-[15px] text-neutral-700 mb-2">
                Tono de piel
              </label>
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setAiTonoPiel("")}
                  className={`px-4 h-10 rounded-full border text-[15px] ${aiTonoPiel === "" ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                >
                  Cualquiera
                </button>
                {SKIN_TONE_OPTIONS.map(({ label, color }) => (
                  <button
                    key={label}
                    type="button"
                    title={label}
                    onClick={() =>
                      setAiTonoPiel(aiTonoPiel === label ? "" : label)
                    }
                    className={`w-9 h-9 rounded-full border ${aiTonoPiel === label ? "ring-2 ring-brand-primary ring-offset-2 border-brand-primary" : "border-neutral-300"} flex items-center justify-center`}
                    style={{ backgroundColor: color }}
                  >
                    {aiTonoPiel === label && (
                      <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[15px] text-neutral-700 mb-2">
                Color de pelo
              </label>
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setAiColorPelo("")}
                  className={`px-4 h-10 rounded-full border text-[15px] ${aiColorPelo === "" ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                >
                  Cualquiera
                </button>
                {HAIR_COLOR_OPTIONS.map(({ label, color }) => (
                  <button
                    key={label}
                    type="button"
                    title={label}
                    onClick={() =>
                      setAiColorPelo(aiColorPelo === label ? "" : label)
                    }
                    className={`w-9 h-9 rounded-full border ${aiColorPelo === label ? "ring-2 ring-brand-primary ring-offset-2 border-brand-primary" : "border-neutral-300"} flex items-center justify-center`}
                    style={{ backgroundColor: color }}
                  >
                    {aiColorPelo === label && (
                      <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[15px] text-neutral-700 mb-2">
                Largo de pelo
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setAiLargoPelo("")}
                  className={`px-4 h-10 rounded-full border text-[15px] ${aiLargoPelo === "" ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                >
                  Cualquiera
                </button>
                {["Rapado", "Corto", "Medio", "Largo"].map((l) =>
                  aiPill(aiLargoPelo, setAiLargoPelo, l),
                )}
              </div>
            </div>
            <div>
              <label className="block text-[15px] text-neutral-700 mb-2">
                Descripción adicional
              </label>
              <textarea
                rows={3}
                value={aiDescripcion}
                onChange={(e) => setAiDescripcion(e.target.value)}
                placeholder="Ej: rulos, sonriendo, ojos celestes, brazos cruzados"
                className="w-full rounded-md border border-neutral-200 p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>
          </div>
          {aiError && <p className="text-[15px] text-error mt-4">{aiError}</p>}
        </>
      )}
    </>
  );

  // Determine which content/title/accept to show
  let sheetTitle = "Modelo guardado";
  let sheetContent = listContent;
  let sheetAcceptDisabled = localSelectedId === null;
  let sheetOnAccept: (() => void) | undefined = handleAccept;
  let sheetOnClose = handleClose;

  if (showAddForm === "chooser") {
    sheetTitle = "Añadir modelo";
    sheetContent = chooserContent;
    sheetAcceptDisabled = true;
    sheetOnAccept = undefined;
    sheetOnClose = () => {
      setShowAddForm(false);
      resetAddState();
    };
  } else if (showAddForm === "real") {
    sheetTitle = "Añadir modelo real";
    sheetContent = realFormContent;
    sheetAcceptDisabled =
      saving || !addName.trim() || !addFile || !consentA || !consentB;
    sheetOnAccept = handleSaveReal;
    sheetOnClose = () => setShowAddForm("chooser");
  } else if (showAddForm === "ia") {
    sheetTitle = "Crear modelo con IA";
    sheetContent = aiFormContent;
    if (aiResultB64) {
      sheetAcceptDisabled = aiSaving;
      sheetOnAccept = handleAiSave;
      sheetOnClose = () => setAiResultB64(null);
    } else {
      sheetAcceptDisabled = !aiName.trim() || aiGenerating;
      sheetOnAccept = handleAiGenerate;
      sheetOnClose = () => setShowAddForm("chooser");
    }
  } else if (showAddForm === "historial") {
    sheetTitle = "Desde imagen generada";
    sheetContent = historialFormContent;
    sheetAcceptDisabled =
      !selectedGenImage || savingModelFromImage || historialSuccess;
    sheetOnAccept = handleSaveFromGenerated;
    sheetOnClose = () => {
      if (selectedGenImage) {
        setSelectedGenImage(null);
      } else {
        setShowAddForm("chooser");
      }
    };
  }

  return (
    <OptionSheet
      open={open}
      onClose={sheetOnClose}
      onAccept={sheetOnAccept}
      title={sheetTitle}
      acceptDisabled={sheetAcceptDisabled}
      acceptLabel={
        showAddForm === "real"
          ? saving
            ? "Guardando..."
            : "Guardar modelo"
          : showAddForm === "ia"
            ? aiResultB64
              ? aiSaving
                ? "Guardando..."
                : "Guardar modelo"
              : "Generar"
            : showAddForm === "historial"
              ? savingModelFromImage
                ? "Analizando..."
                : "Usar este modelo"
              : "Aceptar"
      }
      closeOnAccept={false}
    >
      {sheetContent}
    </OptionSheet>
  );
};

export default ModeloGuardadoOption;
